/**
 * A status, always as icon + label + colour.
 *
 * Never colour alone, and that is not a style preference: two of the four light-mode
 * status colours sit below 3:1 against the surface, and the icon-and-word pairing is
 * exactly what makes them safe to use. It also means the pill survives greyscale
 * printing, forced-colours mode, and any severity of colour blindness.
 */

import type { ServiceStatus, Status } from '../../api/types'
import { cn } from '../../lib/cn'
import { SERVICE_PRESENTATION, STATUS_PRESENTATION, statusText } from '../../lib/status'

type Size = 'sm' | 'md' | 'lg'

const SIZES: Record<Size, { wrapper: string; icon: number; text: string }> = {
  sm: { wrapper: 'gap-1 px-1.5 py-0.5 text-[11px]', icon: 12, text: 'font-medium' },
  md: { wrapper: 'gap-1.5 px-2 py-1 text-xs', icon: 14, text: 'font-semibold' },
  lg: { wrapper: 'gap-2 px-2.5 py-1.5 text-sm', icon: 18, text: 'font-semibold' },
}

interface StatusPillProps {
  status: Status
  size?: Size
  /** Icon only, with the label still announced to screen readers. */
  iconOnly?: boolean
  className?: string
}

export function StatusPill({
  status,
  size = 'md',
  iconOnly = false,
  className,
}: StatusPillProps) {
  const presentation = STATUS_PRESENTATION[status]
  const Icon = presentation.icon
  const spec = SIZES[size]

  return (
    <span
      className={cn(
        'inline-flex items-center rounded-full border',
        spec.wrapper,
        statusText(presentation.token),
        className,
      )}
      style={{
        // A 10% wash of the status colour, so the pill reads as its severity without
        // becoming a saturated block.
        backgroundColor: `color-mix(in oklab, var(--color-${presentation.token}) 12%, transparent)`,
        borderColor: `color-mix(in oklab, var(--color-${presentation.token}) 35%, transparent)`,
      }}
    >
      <Icon size={spec.icon} strokeWidth={2} aria-hidden className="shrink-0" />
      <span className={cn(spec.text, iconOnly && 'sr-only')}>{presentation.label}</span>
    </span>
  )
}

/** The same treatment for a single service check's up/down/unknown. */
export function ServicePill({
  status,
  size = 'sm',
  className,
}: {
  status: ServiceStatus
  size?: Size
  className?: string
}) {
  const presentation = SERVICE_PRESENTATION[status]
  const Icon = presentation.icon
  const spec = SIZES[size]

  return (
    <span
      className={cn(
        'inline-flex items-center',
        spec.wrapper,
        statusText(presentation.token),
        className,
      )}
    >
      <Icon size={spec.icon} strokeWidth={2} aria-hidden className="shrink-0" />
      <span className={spec.text}>{presentation.label}</span>
    </span>
  )
}
